import { useEffect, useRef, useState } from 'react';
import type { ServerMsg } from './intents';
import type { GlobalBus } from './wsBus';
import { recordAck, recordIntentSent, recordResult } from '../utils/metrics';

type ActionState = 'idle' | 'pending' | 'success' | 'error';

interface ActionBus {
  subscribe: GlobalBus['subscribe'];
  sendIntent: GlobalBus['sendIntent'];
}

export function useActionFeedback(bus?: ActionBus | null) {
  const [states, setStates] = useState<Record<string, ActionState>>({});
  const pending = useRef(new Map<string, string>());

  function setState(key: string, next: ActionState) {
    setStates(prev => ({ ...prev, [key]: next }));
  }

  function fire(key: string, name: string, args?: any) {
    if (!bus) {
      console.warn('[useActionFeedback] Missing action bus');
      return undefined;
    }
    const cid = `${key}_${Date.now()}`;
    pending.current.set(cid, key);
    setState(key, 'pending');
    recordIntentSent(cid);
    bus.sendIntent(name, args, cid);
    return cid;
  }

  function handleServerMessage(message: ServerMsg) {
    if (message.t === 'ack') {
      recordAck(message.cid);
      return;
    }
    if (message.t !== 'result') {
      return;
    }
    const key = pending.current.get(message.cid);
    if (!key) {
      return;
    }
    if (!message.ok && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('hivecore:toast', {
        detail: {
          level: 'error',
          message: message.err ? `${message.err} (cid: ${message.cid})` : `Action failed (cid: ${message.cid})`
        }
      }));
    }
    setState(key, message.ok ? 'success' : 'error');
    pending.current.delete(message.cid);
    setTimeout(() => setState(key, 'idle'), 600);
    recordResult(message.cid, message.ok);
  }

  useEffect(() => {
    if (!bus) {
      return undefined;
    }

    const unsubscribe = bus.subscribe((message: ServerMsg) => {
      try {
        handleServerMessage(message);
      } catch (error) {
        console.warn('[useActionFeedback] Failed to process message', error);
      }
    });

    return unsubscribe;
  }, [bus]);

  return { states, fire };
}
